import type { JSX } from 'react'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Copy, Download, ShieldCheck, TriangleAlert } from 'lucide-react'
import type { TotpSetupDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { ApiError } from '@/lib/api-error'
import { useSessionStore } from '@/stores/session'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { Button } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { Dialog } from '@/ui/primitives/Dialog'
import { PasswordField, TextField } from '@/ui/primitives/Field'
import { problemText } from '../shared/problem'
import { QrCode } from './QrCode'

/**
 * Второй фактор (docs/06 §2).
 *
 * Поток намеренно линейный и без «сохранить всё»: подключение — три шага
 * (секрет → код из приложения → recovery-коды), и каждый шаг подтверждается там,
 * где его сделали.
 *
 * Что здесь принципиально:
 *  - полный секрет живёт ТОЛЬКО внутри `otpauthUrl`. Он рисуется QR-кодом и
 *    показывается строкой для ручного ввода — и никуда не логируется;
 *  - `secretMasked` — подпись, по которой сверяют «тот ли это секрет»;
 *  - recovery-коды выдаются ОДИН раз. Пока они на экране, шаг нельзя закрыть
 *    случайным Esc: без них потеря телефона означает потерю доступа;
 *  - выключение требует пароль И код — иначе чужой человек за незапертым
 *    компьютером снимает второй фактор одной кнопкой.
 */

type Stage =
  { name: 'idle' } | { name: 'setup'; data: TotpSetupDto } | { name: 'codes'; codes: string[] }

export function TotpPanel({ enabled }: { enabled: boolean }): JSX.Element {
  const push = useToastStore(state => state.push)
  const setSession = useSessionStore(state => state.setSession)

  const [stage, setStage] = useState<Stage>({ name: 'idle' })
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [disableOpen, setDisableOpen] = useState(false)

  /** Перечитываем `/auth/me`: от `totpEnabled` зависит вид этого же блока. */
  const refreshSession = async (): Promise<void> => setSession(await api.auth.me())

  const setup = useMutation({
    mutationFn: () => api.auth.totpSetup(),
    onSuccess: data => {
      setCode('')
      setError(null)
      setStage({ name: 'setup', data })
    },
    onError: problem => setError(problemText(problem))
  })

  const enable = useMutation({
    mutationFn: () => api.auth.totpEnable(code),
    onSuccess: async result => {
      setError(null)
      setStage({ name: 'codes', codes: result.recoveryCodes })
      await refreshSession()
    },
    onError: problem => {
      // Сервер на неверный код отвечает 401. Общее «не авторизованы» здесь
      // сбивает с толку: пользователь-то вошёл, не сошёлся именно код.
      if (problem instanceof ApiError && problem.code === 'UNAUTHORIZED') {
        setError('Код не подошёл. Проверьте, что время на телефоне точное, и введите свежий код.')
        return
      }
      setError(problemText(problem))
    }
  })

  const finishCodes = (): void => {
    setStage({ name: 'idle' })
    push({ message: 'Второй фактор включён', tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
  }

  return (
    <div className="flex flex-col gap-4 border-t border-line pt-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-base text-ink">
            <ShieldCheck aria-hidden className="size-4 text-ink-muted" />
            Второй фактор (TOTP)
          </p>
          <p className="mt-1 max-w-prose text-2xs text-ink-muted">
            Код из приложения-аутентификатора при входе. Украденного пароля станет недостаточно.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Chip tone={enabled ? 'live' : 'idle'} dot>
            {enabled ? 'Включён' : 'Выключен'}
          </Chip>
          {enabled ? (
            <Button variant="danger" onClick={() => setDisableOpen(true)}>
              Отключить
            </Button>
          ) : stage.name === 'idle' ? (
            <Button variant="primary" loading={setup.isPending} onClick={() => setup.mutate()}>
              Подключить
            </Button>
          ) : null}
        </div>
      </div>

      {stage.name === 'setup' ? (
        <SetupStep
          data={stage.data}
          code={code}
          error={error}
          busy={enable.isPending}
          onCode={value => setCode(value)}
          onSubmit={() => {
            setError(null)
            if (!/^\d{6}$/.test(code)) {
              setError('Код — ровно шесть цифр')
              return
            }
            enable.mutate()
          }}
          onCancel={() => {
            setStage({ name: 'idle' })
            setError(null)
          }}
        />
      ) : null}

      {stage.name === 'codes' ? <RecoveryCodes codes={stage.codes} onDone={finishCodes} /> : null}

      {stage.name === 'idle' && error !== null ? (
        <p
          role="alert"
          className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
        >
          {error}
        </p>
      ) : null}

      <DisableDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onDone={async () => {
          setDisableOpen(false)
          await refreshSession()
          push({ message: 'Второй фактор выключен', tone: 'warning', timeoutMs: DEFAULT_TOAST_MS })
        }}
      />
    </div>
  )
}

// ─────────────────────────── шаг 1–2: секрет и код ───────────────────────────

/** Base32-секрет из `otpauth://`-ссылки — для ручного ввода в аутентификатор. */
function secretOf(otpauthUrl: string): string | null {
  try {
    return new URL(otpauthUrl).searchParams.get('secret')
  } catch {
    return null
  }
}

function SetupStep({
  data,
  code,
  error,
  busy,
  onCode,
  onSubmit,
  onCancel
}: {
  data: TotpSetupDto
  code: string
  error: string | null
  busy: boolean
  onCode: (value: string) => void
  onSubmit: () => void
  onCancel: () => void
}): JSX.Element {
  const [manual, setManual] = useState(false)
  const secret = secretOf(data.otpauthUrl)

  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-line bg-panel-2 p-4"
      noValidate
      onSubmit={event => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <div className="flex flex-wrap items-start gap-5">
        <div className="shrink-0">
          {/* Если строка вдруг не влезет в QR, компонент вернёт null — тогда
              остаётся ручной ввод ниже, и шаг всё равно проходится. */}
          <QrCode value={data.otpauthUrl} className="rounded-sm" />
        </div>

        <div className="flex min-w-60 flex-1 flex-col gap-3">
          <div>
            <p className="text-base text-ink">Отсканируйте код в приложении</p>
            <p className="mt-1 max-w-prose text-2xs text-ink-muted">
              Подойдёт любой TOTP-аутентификатор: Google Authenticator, Aegis, 1Password, KeePassXC.
            </p>
          </div>

          <p className="text-2xs text-ink-muted">
            Отпечаток секрета: <span className="font-mono text-ink">{data.secretMasked}</span> — по
            нему видно, что в приложение попал именно этот секрет.
          </p>

          {manual ? (
            <div className="flex flex-col gap-1.5">
              <p className="text-2xs text-ink-muted">
                Введите вручную (тип — «по времени», 6 цифр, 30 секунд):
              </p>
              <code className="rounded-sm border border-line bg-panel-3 px-2.5 py-2 font-mono text-base break-all text-ink select-all">
                {secret ?? data.otpauthUrl}
              </code>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => setManual(true)}
            >
              Не получается отсканировать
            </Button>
          )}

          <TextField
            label="Код из приложения"
            hint="Шесть цифр, обновляются каждые 30 секунд"
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            mono
            value={code}
            required
            error={error}
            onChange={event => onCode(event.target.value.replace(/\D/gu, ''))}
          />

          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" loading={busy}>
              Включить второй фактор
            </Button>
            <Button variant="ghost" onClick={onCancel}>
              Отмена
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

// ─────────────────────────── шаг 3: recovery-коды ───────────────────────────

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }): JSX.Element {
  const push = useToastStore(state => state.push)
  const [acknowledged, setAcknowledged] = useState(false)
  const text = codes.join('\n')

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)
      push({ message: 'Коды скопированы', tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
    } catch {
      // Буфер может быть запрещён политикой браузера — коды всё равно на экране.
      push({
        message: 'Браузер не дал доступ к буферу обмена',
        tone: 'warning',
        timeoutMs: DEFAULT_TOAST_MS
      })
    }
  }

  /**
   * Скачивание — через Blob и `object:`-ссылку. Никаких внешних хостов: коды
   * восстановления не должны покидать браузер даже ради «удобного» сервиса.
   */
  const download = (): void => {
    const blob = new Blob([`${text}\n`], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'apiary-recovery-codes.txt'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section className="flex flex-col gap-4 rounded-md border border-warn/40 bg-warn/12 p-4">
      <div className="flex items-start gap-2.5">
        <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warn" />
        <div className="min-w-0">
          {/* role="alert": экран сменился сам, и скринридер обязан об этом
              сказать — иначе пользователь закроет одноразовые коды не читая. */}
          <p role="alert" className="text-base text-warn">
            Сохраните коды восстановления — второй раз их не покажет никто
          </p>
          <p className="mt-1 max-w-prose text-2xs text-ink-muted">
            Каждый код срабатывает один раз и заменяет код из приложения, если телефон потерян. Вход
            по такому коду полностью выключает второй фактор — его придётся настроить заново.
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
        {codes.map(item => (
          <li
            key={item}
            className="rounded-sm border border-line bg-panel px-2 py-1.5 text-center font-mono text-base text-ink select-all"
          >
            {item}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={() => void copy()}>
          <Copy aria-hidden className="size-4" />
          Скопировать
        </Button>
        <Button variant="secondary" onClick={download}>
          <Download aria-hidden className="size-4" />
          Скачать файлом
        </Button>
      </div>

      <label className="flex items-start gap-2.5 text-base text-ink">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={event => setAcknowledged(event.target.checked)}
          className="mt-1 size-4 shrink-0 accent-honey"
        />
        Я сохранил коды в надёжном месте
      </label>

      {/* Кнопка «Готово» до галочки заблокирована: закрыть одноразовые коды
          случайным кликом — это потеря доступа к аккаунту, а не мелкая ошибка. */}
      <Button variant="primary" className="self-start" disabled={!acknowledged} onClick={onDone}>
        Готово
      </Button>
    </section>
  )
}

// ─────────────────────────────── выключение ───────────────────────────────

function DisableDialog({
  open,
  onClose,
  onDone
}: {
  open: boolean
  onClose: () => void
  onDone: () => Promise<void>
}): JSX.Element {
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const disable = useMutation({
    mutationFn: () => api.auth.totpDisable(password, code.trim()),
    onSuccess: async () => {
      setPassword('')
      setCode('')
      setError(null)
      await onDone()
    },
    onError: problem => {
      if (problem instanceof ApiError && problem.code === 'UNAUTHORIZED') {
        setError('Пароль или код не подошли')
        return
      }
      setError(problemText(problem))
    }
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Отключить второй фактор?"
      note="Дальше для входа будет достаточно пароля"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            loading={disable.isPending}
            onClick={() => {
              setError(null)
              if (password === '' || code.trim() === '') {
                setError('Нужны и пароль, и код')
                return
              }
              disable.mutate()
            }}
          >
            Отключить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <PasswordField
          label="Текущий пароль"
          autoComplete="current-password"
          value={password}
          required
          onChange={event => setPassword(event.target.value)}
        />
        <TextField
          label="Код подтверждения"
          hint="Шесть цифр из приложения или код восстановления"
          autoComplete="one-time-code"
          mono
          value={code}
          required
          onChange={event => setCode(event.target.value)}
        />
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {error}
          </p>
        )}
      </div>
    </Dialog>
  )
}
